/**
 * @file useAnalyze - Hook for analyzing messages with optimistic updates
 *
 * This hook wraps the useAnalyzeMutation from @dramadetector/api-client
 * and integrates it with the Zustand analysis store for:
 * - Optimistic UI updates (add to history immediately)
 * - Automatic state management (update on success/error)
 * - Persistent history via AsyncStorage
 */

import { useCallback } from 'react';
import { useAnalyzeMutation, getAuthStatus } from '@dramadetector/api-client';
import { useAnalysisQueryCache } from './useAnalysisQueryCache';
import { useAnalysisStore, type ContextType } from '../../../store';
import { useAnalysisAnalytics } from './useAnalysisAnalytics';
import { getDeviceId } from '@dramadetector/shared/device/storage';

import { confirmAnalysisReceipt } from './utils/confirmAnalysisReceipt';
import { logNetworkError } from '../../../lib/logNetworkError';

/**
 * Options for the useAnalyze hook
 */
export interface UseAnalyzeOptions {
  /**
   * User ID for cache isolation (REQUIRED for security)
   *
   * SECURITY FIX (2025-12-01): Query keys now include userId to prevent
   * cross-user data leakage. Without this, User B would see User A's data
   * in History/Stats after logout/login on the same browser.
   *
   * @example
   * ```tsx
   * const { user } = useAuth();
   * const { analyze } = useAnalyze({ userId: user?.id });
   * ```
   */
  userId?: string;
  /** DRAMA-WEB-28: Callback to store clearance token from backend response */
  onClearanceToken?: (token: string) => void;
}

/**
 * Return type for the useAnalyze hook
 */
export interface UseAnalyzeReturn {
  /**
   * Analyze a message with the given context.
   * Returns the ID of the analysis item for tracking.
   *
   * @param message - The message to analyze
   * @param context - The context type (family, work, friends, etc.)
   * @param turnstileToken - Optional Cloudflare Turnstile token for anonymous trial (Phase 1.6)
   * @param clearanceToken - Optional DRAMA-WEB-28 clearance token (skips Turnstile)
   */
  analyze: (message: string, context: ContextType, turnstileToken?: string, clearanceToken?: string) => string;

  /**
   * Whether the analysis is currently pending
   */
  isPending: boolean;

  /**
   * Whether the last analysis resulted in an error
   */
  isError: boolean;

  /**
   * The data from the last successful analysis
   */
  data: ReturnType<typeof useAnalyzeMutation>['data'];

  /**
   * The error from the last failed analysis
   */
  error: ReturnType<typeof useAnalyzeMutation>['error'];

  /**
   * Reset the mutation state (clears data and error)
   */
  reset: () => void;
}

/**
 * Hook for analyzing messages with optimistic updates and persistent history.
 *
 * This hook:
 * 1. Adds the analysis to history immediately (optimistic update)
 * 2. Calls the API via useAnalyzeMutation
 * 3. Updates the history item with the result (success or error)
 * 4. Persists all history to AsyncStorage automatically
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { analyze, isPending, data, error } = useAnalyze();
 *
 *   // For authenticated users
 *   const handleAnalyze = () => {
 *     const id = analyze('Is this message dramatic?', 'family');
 *     console.log('Analysis ID:', id);
 *   };
 *
 *   // For anonymous users (Phase 1.6)
 *   const handleAnonymousAnalyze = (turnstileToken: string) => {
 *     const id = analyze('Is this message dramatic?', 'family', turnstileToken);
 *     console.log('Analysis ID:', id);
 *   };
 *
 *   return (
 *     <View>
 *       <Button onPress={handleAnalyze} disabled={isPending}>
 *         Analyze
 *       </Button>
 *       {isPending && <Text>Analyzing...</Text>}
 *       {error && <Text>Error: {error.message}</Text>}
 *       {data && <Text>Score: {data.data?.score}</Text>}
 *       {data?.data?.quota && (
 *         <Text>Analyses remaining: {data.data.quota.remaining}</Text>
 *       )}
 *     </View>
 *   );
 * }
 * ```
 *
 * @returns {UseAnalyzeReturn} Analysis function and state
 */
export function useAnalyze(options?: UseAnalyzeOptions): UseAnalyzeReturn {
  const { userId, onClearanceToken } = options || {};

  const { cacheAnalysisResult, invalidateDeviceQuota } = useAnalysisQueryCache();
  const { trackStart, trackSuccess, trackFailure } = useAnalysisAnalytics();
  const addAnalysis = useAnalysisStore((state) => state.addAnalysis);
  const updateAnalysis = useAnalysisStore((state) => state.updateAnalysis);
  const setCurrentAnalysis = useAnalysisStore((state) => state.setCurrentAnalysis);
  const getById = useAnalysisStore((state) => state.getById);

  // SECURITY FIX (2025-12-01): Pass userId for user-scoped cache invalidation
  // This ensures only the current user's queries are invalidated/refetched
  // Multi-model consensus: Gemini Pro 3 (10/10), o3 (8/10) confirmed this approach
  const { mutate, isPending, isError, data, error, reset } = useAnalyzeMutation({
    userId,
  });

  const analyze = useCallback(
    (message: string, context: ContextType, turnstileToken?: string, clearanceToken?: string): string => {
      const startTime = Date.now();

      // NocoDB #3610: derive authoritative anonymous state from the api-client
      // auth state machine, not from the caller-provided `options.userId`.
      //
      // The home tab (app/(tabs)/index.tsx) passes `userId: user?.id` from
      // useAuth(). If the component mounts before AuthContext finishes resolving
      // the BA session cookie, `user?.id` is undefined and the hook was treating
      // the caller as anonymous — even though a session is active. That produced
      // DRAMA-WEB-2S: the mutation went out as anonymous, the worker rejected
      // with "Valid authentication token required", and the error surfaced to
      // Sentry with a misleading `isAuthenticated: false` tag.
      //
      // By reading `getAuthStatus()` at click time we get the single source of
      // truth for whether there's an active session, independent of when the
      // caller re-rendered.
      const authStatus = getAuthStatus();
      const effectiveIsAnonymous = authStatus !== 'authenticated';

      // Short-circuit if auth is still pending. A pending status means BA has
      // a session but the JWT isn't yet configured. Firing the mutation here
      // races the bearer-token injection and reliably 401s. Surface a clear
      // error so the caller can retry once auth settles (usually ~1s).
      if (authStatus === 'pending') {
        const raceId = addAnalysis({
          message,
          context,
          result: null,
          status: 'error',
          error: 'Still signing in — please try again in a moment.',
        });
        return raceId;
      }

      // Track analysis initiation (PostHog, Sentry, breadcrumbs, transaction)
      // (extracted to useAnalysisAnalytics.ts, S3.3)
      const transaction = trackStart(context, message);

      // Optimistic update: Add to history immediately with pending status
      const id = addAnalysis({
        message,
        context,
        result: null,
        status: 'pending',
      });

      // Set as current analysis
      const currentItem = getById(id);
      if (currentItem) {
        setCurrentAnalysis(currentItem);
      }

      // P0 FIX (2026-01-20): Generate idempotency token at user intent level
      // This ensures ONE token per button click, preventing multiple quota deductions
      // if the user accidentally double-clicks or the component re-renders.
      // The token is stable across automatic retries for this specific analysis request.
      const idempotencyToken = crypto.randomUUID();

      // Call API with optional Turnstile token (Phase 1.6) or clearance token (DRAMA-WEB-28)
      mutate(
        { message, context, turnstileToken, clearanceToken, idempotencyToken },
        {
          onSuccess: (response) => {
            const duration = Date.now() - startTime;

            // Track analysis success (PostHog, Sentry, breadcrumbs, span)
            // (extracted to useAnalysisAnalytics.ts, S3.3)
            trackSuccess(transaction, response, context, message, duration);

            // Update history item with successful result
            updateAnalysis(id, {
              result: response.data,
              status: 'success',
            });

            // Update current analysis
            const updatedItem = getById(id);
            if (updatedItem) {
              setCurrentAnalysis(updatedItem);
            }

            // ============================================================================
            // DRAMA-WEB-28: Extract clearance token from response
            // ============================================================================
            // After first Turnstile verification, backend issues a 30-minute JWT.
            // Store it so subsequent requests skip the Turnstile siteverify call.
            // ============================================================================
            if (response.data?.clearanceToken && onClearanceToken) {
              onClearanceToken(response.data.clearanceToken);
              console.log('[useAnalyze] Clearance token stored for subsequent requests');
            }

            // ============================================================================
            // PHASE A: Store analysis with message/context in React Query cache
            // ============================================================================
            // FIX (2026-02-09): Cache for ALL users, not just anonymous.
            // Include `message` and `context` so useAnalysisResult can retrieve them
            // from the cache hit path without falling back to Zustand or DB fetch.
            //
            // Previously only set for anonymous users, which caused:
            // - Authenticated users always missed cache → unnecessary DB round-trip
            // - DB fetch depends on successful server-side decryption
            // - If message field was lost, inline annotations wouldn't render
            //   (guard: `{hasIssues && message && ...}`)
            //
            // Anonymous-specific gcTime extension is preserved below.
            // ============================================================================
            // Cache result and extend TTL for anonymous users
            // (extracted to useAnalysisQueryCache.ts, S3.2)
            cacheAnalysisResult(response, message, context, effectiveIsAnonymous);

            // ============================================================================
            // PHASE B: Client-Side Confirmation (Quota Refund Protection)
            // ============================================================================
            // CRITICAL: Confirm successful receipt of analysis results to prevent quota refunds.
            //
            // Problem: Server deducts quota BEFORE confirming client receipt. If network fails
            // AFTER quota deduction but BEFORE client caches data, quota is permanently lost.
            //
            // Solution: Call /confirm-analysis-receipt AFTER successful cache update.
            // Background job refunds quota for unconfirmed analyses after 5 minutes.
            //
            // Flow:
            // 1. Server deducts quota, returns analysis
            // 2. Client caches data successfully (we're here now)
            // 3. Client calls /confirm-analysis-receipt
            // 4. Server marks as confirmed
            // 5. Background job skips confirmed entries, refunds unconfirmed after 5 min
            //
            // Multi-Model Consensus: Gemini 2.5 Pro (10/10 confidence)
            // ============================================================================
            if (response.data?.id) {
              // Get deviceId from localStorage (async operation)
              const confirmedAnalysisId = response.data.id;
              getDeviceId().then((deviceId) => {
                // Fire-and-forget confirmation ping (don't block UI)
                confirmAnalysisReceipt(confirmedAnalysisId, userId, deviceId)
                  .then((confirmed) => {
                    console.log('[useAnalyze] Receipt confirmation sent:', {
                      analysisId: confirmedAnalysisId.substring(0, 8) + '...',
                      confirmed,
                      isAnonymous: effectiveIsAnonymous,
                      hasDeviceId: !!deviceId,
                    });
                  })
                  .catch((confirmError) => {
                    // Non-blocking: If confirmation fails, background job will refund quota.
                    // Intentionally console.warn (not logNetworkError) — receipt confirmation
                    // is a non-critical fire-and-forget path; escalating to console.error
                    // via logNetworkError would create false-positive Sentry noise.
                    console.warn('[useAnalyze] Receipt confirmation failed (non-blocking):', confirmError);
                  });
              }).catch((deviceIdError) => {
                // getDeviceId is a local storage operation, not a network call
                console.error('[useAnalyze] Failed to get deviceId:', deviceIdError);
              });
            }

            // ============================================================================
            // PHASE C: Update Quota Cache for Anonymous Users (2026-01-19 P0 Fix)
            // ============================================================================
            // CRITICAL FIX: Anonymous users don't have Realtime subscriptions for quota updates.
            // After removing checkQuotaUnified() premature check, quota info only comes from
            // analysis response. We MUST invalidate quota cache to trigger refetch with new data.
            //
            // Why invalidate instead of setQueryData?
            // - Server is source of truth for quota (atomic Redis increments)
            // - Invalidate forces refetch, ensuring consistency
            // - Simpler than extracting anonymousQuota and manually updating cache
            //
            // Flow:
            // 1. Analysis completes, server deducts quota
            // 2. Client receives response (we're here)
            // 3. Invalidate quota cache
            // 4. useQuota refetches from get_anonymous_quota RPC
            // 5. UI shows updated quota (e.g., "9 of 10 remaining")
            // ============================================================================
            // Invalidate quota cache for anonymous users
            // (extracted to useAnalysisQueryCache.ts, S3.2)
            if (effectiveIsAnonymous && response.data) {
              invalidateDeviceQuota();
            }

          },
          onError: (err) => {
            logNetworkError(err, {
              hook: 'useAnalyze',
              endpoint: 'analyze-drama',
              isAuthenticated: !effectiveIsAnonymous,
            });

            const duration = Date.now() - startTime;

            // Track analysis failure (PostHog, Sentry, breadcrumbs, span)
            // (extracted to useAnalysisAnalytics.ts, S3.3)
            trackFailure(transaction, err, context, message, duration);

            // Update history item with error
            // Phase 3 Observability: Include full errorInfo for trace display
            updateAnalysis(id, {
              status: 'error',
              error: err.message || 'An unexpected error occurred',
              errorInfo: {
                message: err.message || 'An unexpected error occurred',
                code: err.code,
                requestId: err.requestId,
                timestamp: err.timestamp,
              },
            });

            console.log('[useAnalyze] Updated history with error');

            // Update current analysis
            const updatedItem = getById(id);
            if (updatedItem) {
              setCurrentAnalysis(updatedItem);
              console.log('[useAnalyze] Set current analysis with error');
            }
          },
        }
      );

      return id;
    },
    [addAnalysis, updateAnalysis, setCurrentAnalysis, getById, mutate, onClearanceToken, userId, cacheAnalysisResult, invalidateDeviceQuota, trackStart, trackSuccess, trackFailure]
  );

  return {
    analyze,
    isPending,
    isError,
    data,
    error,
    reset,
  };
}
